import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Icon,
  ConfirmModal,
  FormDrawer,
} from "../../../Application/Admin/TeamsGroups/sharedComponents";
import "./BroadcastDetails.scss";
import {
  getBroadcastByIdApi,
  updateBroadcastApi,
  updateBroadcastStatusApi,
  deleteBroadcastApi,
  getPresignedUploadUrlApi,
  registerMediaApi,
} from "../../../../api/api";

const BROADCAST_TYPE_OPTIONS = [
  { value: "circular", label: "Circular" },
  { value: "announcement", label: "Announcement" },
  { value: "alert", label: "Alert" },
  { value: "reminder", label: "Reminder" },
  { value: "emergency", label: "Emergency" },
];

const ATTACHMENT_TYPES = [
  { value: "", label: "None" },
  { value: "pdf", label: "PDF Document" },
  { value: "image", label: "Image" },
  { value: "video", label: "Video" },
  { value: "audio", label: "Audio" },
];

const STATUS_BADGE_NOTE = {
  draft: "Saved as draft.",
  pending_approval: "Awaiting approval.",
  approved: "Approved, ready to publish.",
  published: "Published and delivered.",
  scheduled: "Scheduled for later delivery.",
  cancelled: "Cancelled.",
};

const getBroadcastFields = () => [
  {
    name: "broadcastType",
    label: "Broadcast Type",
    type: "select",
    options: BROADCAST_TYPE_OPTIONS,
    defaultValue: "circular",
  },
  {
    name: "priority",
    label: "Priority",
    type: "select",
    options: [
      { value: "normal", label: "Normal" },
      { value: "important", label: "Important" },
      { value: "critical", label: "Critical" },
    ],
    defaultValue: "normal",
  },
  {
    name: "title",
    label: "Title",
    type: "text",
    placeholder: "e.g. System Maintenance Notice",
    required: true,
  },
  {
    name: "body",
    label: "Message Body",
    type: "textarea",
    rows: 4,
    placeholder: "Enter the broadcast message...",
    required: true,
  },
  {
    name: "attachmentType",
    label: "Attachment Type",
    type: "select",
    options: ATTACHMENT_TYPES,
    defaultValue: "",
  },
  {
    name: "attachmentFile",
    label: "Upload Attachment",
    type: "custom",
    visible: (form) => form.attachmentType && form.attachmentType !== "",
    render: ({ form, updateField }) => (
      <div className="attachment-upload">
        {form.attachmentFile ? (
          <div className="attachment-upload__preview">
            <div className="attachment-upload__file-info">
              <Icon name="file" size={20} />
              <span>{form.attachmentFile.name || form.attachmentName}</span>
              {form.attachmentFile.size && (
                <span className="attachment-upload__file-size">
                  ({(form.attachmentFile.size / 1024 / 1024).toFixed(2)} MB)
                </span>
              )}
            </div>
            {form.uploading && (
              <span style={{ fontSize: "12px", color: "#0062FE" }}>
                Uploading...
              </span>
            )}
            <button
              type="button"
              className="link-btn link-btn--danger"
              onClick={() => {
                updateField("attachmentFile", null);
                updateField("attachmentName", "");
                updateField("attachmentUrl", "");
                updateField("uploading", false);
              }}
              disabled={form.uploading}
            >
              Remove
            </button>
          </div>
        ) : form.attachmentUrl ? (
          <div className="attachment-upload__preview">
            <div className="attachment-upload__file-info">
              <Icon name="file" size={20} />
              <span>{form.attachmentName || "Attachment"}</span>
            </div>
            <button
              type="button"
              className="link-btn link-btn--danger"
              onClick={() => {
                updateField("attachmentFile", null);
                updateField("attachmentName", "");
                updateField("attachmentUrl", "");
                updateField("attachmentType", "");
              }}
            >
              Remove
            </button>
          </div>
        ) : (
          <div className="attachment-upload__zone">
            <input
              type="file"
              id="broadcast-attachment-edit"
              onChange={(e) => {
                const file = e.target.files[0];
                if (file) {
                  updateField("attachmentFile", file);
                  updateField("attachmentName", file.name);
                  updateField("uploading", false);
                }
              }}
              accept={
                form.attachmentType === "pdf"
                  ? ".pdf"
                  : form.attachmentType === "image"
                    ? "image/*"
                    : form.attachmentType === "video"
                      ? "video/*"
                      : form.attachmentType === "audio"
                        ? "audio/*"
                        : "*/*"
              }
              style={{ display: "none" }}
            />
            <label
              htmlFor="broadcast-attachment-edit"
              className="attachment-upload__label"
            >
              <Icon name="plus" size={16} />
              <span>
                Choose{" "}
                {
                  ATTACHMENT_TYPES.find((t) => t.value === form.attachmentType)
                    ?.label
                }{" "}
                File
              </span>
            </label>
            <span className="field-hint">
              {form.attachmentType === "pdf" && "PDF up to 10MB"}
              {form.attachmentType === "image" && "JPG, PNG, GIF up to 5MB"}
              {form.attachmentType === "video" && "MP4, MOV up to 50MB"}
              {form.attachmentType === "audio" && "MP3, WAV up to 10MB"}
            </span>
          </div>
        )}
      </div>
    ),
  },
  {
    name: "scheduleTime",
    label: "Schedule (optional)",
    type: "datetime-local",
  },
  {
    name: "oneTimeView",
    label: "One-Time View (Disappears after reading)",
    type: "toggle",
    defaultValue: false,
  },
  {
    name: "acknowledgementRequired",
    label: "Acknowledgement Required",
    type: "toggle",
    defaultValue: false,
  },
];

function BroadcastDetails() {
  const { id, layout } = useParams();
  const navigate = useNavigate();
  const [broadcast, setBroadcast] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editDrawerOpen, setEditDrawerOpen] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    fetchBroadcast();
  }, [id]);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 3000);
    return () => clearTimeout(t);
  }, [toast]);

  const fetchBroadcast = async () => {
    setLoading(true);
    try {
      const res = await getBroadcastByIdApi(id);
      if (res.data.success) setBroadcast(res.data.broadcast);
    } catch (err) {
      console.error("Fetch broadcast error:", err);
    } finally {
      setLoading(false);
    }
  };

  // Upload attachment to MinIO
  const uploadAttachmentToMinio = async (file) => {
    try {
      const presignedRes = await getPresignedUploadUrlApi({
        fileName: file.name,
        mimeType: file.type,
        folder: "broadcast-attachments",
      });

      if (!presignedRes.data.success) {
        throw new Error("Failed to get upload URL");
      }

      const { uploadUrl, objectKey, fileUrl } = presignedRes.data;

      await axios.put(uploadUrl, file, {
        headers: { "Content-Type": file.type },
      });

      await registerMediaApi({
        fileName: file.name,
        mimeType: file.type,
        objectKey,
        publicUrl: fileUrl,
        fileSize: file.size,
        sourceType: "BROADCAST",
        matrixRoomId: "broadcast",
      });

      return {
        attachmentUrl: fileUrl,
        attachmentType: file.type.startsWith("image/")
          ? "image"
          : file.type === "application/pdf"
            ? "pdf"
            : file.type.startsWith("video/")
              ? "video"
              : file.type.startsWith("audio/")
                ? "audio"
                : "other",
        attachmentName: file.name,
      };
    } catch (err) {
      console.error("Upload attachment error:", err);
      throw err;
    }
  };

  const handleEditSubmit = async (formData) => {
    try {
      let attachmentData = {};

      // Upload new attachment if present
      if (formData.attachmentFile && formData.attachmentFile instanceof File) {
        attachmentData = await uploadAttachmentToMinio(formData.attachmentFile);
      }

      const payload = {
        title: formData.title.trim(),
        body: formData.body.trim(),
        broadcastType: formData.broadcastType,
        priority: formData.priority,
        scheduleTime: formData.scheduleTime || null,
        oneTimeView: formData.oneTimeView || false,
        acknowledgementRequired: formData.acknowledgementRequired || false,
        ...attachmentData,
      };

      const res = await updateBroadcastApi(broadcast.id, payload);
      if (res.data.success) {
        setBroadcast(res.data.broadcast);
        setToast("Broadcast updated.");
        setEditDrawerOpen(false);
        fetchBroadcast();
      }
    } catch (err) {
      console.error("Update broadcast error:", err);
      setToast("Failed to update broadcast");
    }
  };

  const handleStatusChange = async (status) => {
    try {
      await updateBroadcastStatusApi(broadcast.id, { status });
      setToast(`Broadcast ${status.replace("_", " ")}.`);
      fetchBroadcast();
    } catch (err) {
      console.error("Status change error:", err);
    }
  };

  const handleDeleteBroadcast = () => {
    setConfirm({
      title: "Delete Broadcast",
      message: `Are you sure you want to delete "${broadcast.title}"? This action cannot be undone.`,
      confirmLabel: "Yes, Delete",
      danger: true,
      onConfirm: async () => {
        try {
          await deleteBroadcastApi(broadcast.id);
          setToast("Broadcast deleted successfully.");
          navigate(`/broadcasts/${layout}`);
        } catch (err) {
          console.error("Delete broadcast error:", err);
          setToast("Failed to delete broadcast");
        }
        setConfirm(null);
      },
    });
  };

  const getTypeBadgeClass = (type) =>
    ({
      circular: "badge--type-circular",
      announcement: "badge--type-announcement",
      alert: "badge--type-alert",
      reminder: "badge--type-reminder",
      emergency: "badge--type-emergency",
    })[type] || "";

  const getPriorityBadgeClass = (p) =>
    ({
      normal: "badge--priority-normal",
      important: "badge--priority-important",
      critical: "badge--priority-critical",
    })[p] || "";

  const getStatusBadgeClass = (s) =>
    ({
      draft: "badge--status-draft",
      pending_approval: "badge--status-pending_approval",
      approved: "badge--status-approved",
      published: "badge--status-published",
      scheduled: "badge--status-scheduled",
      cancelled: "badge--status-cancelled",
    })[s] || "";

  const getAttachmentIcon = (type) => {
    if (!type) return "file";
    if (type === "image") return "file";
    if (type === "pdf") return "file";
    if (type === "video") return "file";
    if (type === "audio") return "file";
    return "file";
  };

  if (loading)
    return (
      <div className="broadcast-details-page">
        <div className="empty-state">
          <p>Loading...</p>
        </div>
      </div>
    );

  if (!broadcast)
    return (
      <div className="broadcast-details-page">
        <div className="empty-state">
          <p>Broadcast not found.</p>
        </div>
      </div>
    );

  return (
    <div className="broadcast-details-page">
      <div className="broadcast-details__header">
        <button
          className="broadcast-details__back"
          onClick={() => navigate(`/broadcasts/${layout}`)}
        >
          <Icon name="chevronLeft" size={16} /> Back to Broadcasts
        </button>
        <div className="broadcast-details__title-row">
          <div className="broadcast-details__title-block">
            <div className="broadcast-details__badges">
              <span
                className={`badge ${getTypeBadgeClass(broadcast.broadcastType)}`}
              >
                {broadcast.broadcastType}
              </span>
              <span
                className={`badge ${getStatusBadgeClass(broadcast.status)}`}
              >
                {broadcast.status?.replace("_", " ")}
              </span>
              <span
                className={`badge ${getPriorityBadgeClass(broadcast.priority)}`}
              >
                {broadcast.priority}
              </span>
              {broadcast.oneTimeView && (
                <span className="badge badge--type-emergency">
                  One-Time View
                </span>
              )}
            </div>
            <h1 className="broadcast-details__title">{broadcast.title}</h1>
            <p className="broadcast-details__meta">
              Created by {broadcast.createdBy} on{" "}
              {new Date(broadcast.createdAt).toLocaleString()}
            </p>
          </div>
        </div>
      </div>

      <div className="broadcast-details-grid">
        <div className="broadcast-details-grid__main">
          {/* Message Card */}
          <div className="detail-card">
            <h2 className="detail-card__title">Message</h2>
            <div className="broadcast-details__body">
              {broadcast.body || broadcast.message}
            </div>
          </div>

          {/* Attachment Card */}
          {broadcast.attachmentUrl && (
            <div className="detail-card">
              <h2 className="detail-card__title">Attachment</h2>
              <div className="attachment-display">
                <div className="attachment-display__info">
                  <Icon
                    name={getAttachmentIcon(broadcast.attachmentType)}
                    size={24}
                  />
                  <div>
                    <span className="attachment-display__name">
                      {broadcast.attachmentName || "Attachment"}
                    </span>
                    <span className="attachment-display__type">
                      {broadcast.attachmentType?.toUpperCase() || "File"}
                    </span>
                  </div>
                </div>
                {broadcast.attachmentType === "image" && (
                  <div className="attachment-display__preview">
                    <img
                      src={broadcast.attachmentUrl}
                      alt={broadcast.attachmentName}
                      style={{
                        maxWidth: "100%",
                        maxHeight: "300px",
                        borderRadius: "8px",
                      }}
                    />
                  </div>
                )}
                <a
                  href={broadcast.attachmentUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn--secondary"
                  style={{ marginTop: "12px" }}
                >
                  <Icon name="file" size={14} /> View / Download
                </a>
              </div>
            </div>
          )}

          {/* Overview Card */}
          <div className="detail-card">
            <h2 className="detail-card__title">Overview</h2>
            <div className="detail-row">
              <span className="detail-row__label">Type</span>
              <span className="detail-row__value">
                {broadcast.broadcastType}
              </span>
            </div>
            <div className="detail-row">
              <span className="detail-row__label">Priority</span>
              <span
                className={`badge ${getPriorityBadgeClass(broadcast.priority)}`}
              >
                {broadcast.priority}
              </span>
            </div>
            <div className="detail-row">
              <span className="detail-row__label">Status</span>
              <span
                className={`badge ${getStatusBadgeClass(broadcast.status)}`}
              >
                {broadcast.status?.replace("_", " ")}
              </span>
            </div>
            <div className="detail-row">
              <span className="detail-row__label">Target</span>
              <span className="detail-row__value">{broadcast.targetName}</span>
            </div>
            {broadcast.targetDepartment && (
              <div className="detail-row">
                <span className="detail-row__label">Department</span>
                <span className="detail-row__value">
                  {broadcast.targetDepartment}
                </span>
              </div>
            )}
            {broadcast.targetTeam && (
              <div className="detail-row">
                <span className="detail-row__label">Team</span>
                <span className="detail-row__value">
                  {broadcast.targetTeam}
                </span>
              </div>
            )}
            <div className="detail-row">
              <span className="detail-row__label">One-Time View</span>
              <span className="detail-row__value">
                {broadcast.oneTimeView
                  ? "Yes - Disappears after reading"
                  : "No"}
              </span>
            </div>
            <div className="detail-row">
              <span className="detail-row__label">Acknowledgement</span>
              <span className="detail-row__value">
                {broadcast.acknowledgementRequired
                  ? "Required"
                  : "Not Required"}
              </span>
            </div>
            {broadcast.scheduleTime && (
              <div className="detail-row">
                <span className="detail-row__label">Scheduled</span>
                <span className="detail-row__value">
                  {new Date(broadcast.scheduleTime).toLocaleString()}
                </span>
              </div>
            )}
          </div>

          {/* Stats Card */}
          {broadcast.stats &&
            (broadcast.stats.delivered > 0 ||
              broadcast.status === "published") && (
              <div className="detail-card">
                <h2 className="detail-card__title">Delivery Stats</h2>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "12px",
                  }}
                >
                  <div className="detail-row">
                    <span className="detail-row__label">Delivered</span>
                    <span className="detail-row__value">
                      {broadcast.stats.delivered}
                    </span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-row__label">Read</span>
                    <span className="detail-row__value">
                      {broadcast.stats.read}
                    </span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-row__label">Acknowledged</span>
                    <span className="detail-row__value">
                      {broadcast.stats.acknowledged}
                    </span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-row__label">Failed</span>
                    <span className="detail-row__value">
                      {broadcast.stats.failed}
                    </span>
                  </div>
                </div>
              </div>
            )}
        </div>

        <div className="broadcast-details-grid__side">
          {/* Status Card */}
          <div className="detail-card detail-card--status">
            <h2 className="detail-card__title">Status</h2>
            <div className="status-block">
              <span
                className={`badge badge--lg ${getStatusBadgeClass(broadcast.status)}`}
              >
                {broadcast.status?.replace("_", " ")}
              </span>
              <p className="status-block__note">
                {STATUS_BADGE_NOTE[broadcast.status]}
              </p>
            </div>
            {broadcast.approvedBy && (
              <div style={{ marginTop: "12px" }}>
                <span className="detail-row__label">Approved by</span>
                <span className="detail-row__value">
                  {broadcast.approvedBy}
                </span>
              </div>
            )}
            {broadcast.publishedAt && (
              <div style={{ marginTop: "8px" }}>
                <span className="detail-row__label">Published</span>
                <span className="detail-row__value">
                  {new Date(broadcast.publishedAt).toLocaleString()}
                </span>
              </div>
            )}
          </div>

          {/* Actions Card */}
          <div className="detail-card">
            <h2 className="detail-card__title">Actions</h2>
            <div
              style={{ display: "flex", flexDirection: "column", gap: "8px" }}
            >
              <button
                className="btn btn--secondary btn-full"
                onClick={() => setEditDrawerOpen(true)}
              >
                <Icon name="edit" size={14} /> Edit Broadcast
              </button>
              {broadcast.status === "draft" && (
                <button
                  className="btn btn--primary btn-full"
                  onClick={() => handleStatusChange("pending_approval")}
                >
                  Submit for Approval
                </button>
              )}
              {broadcast.status === "pending_approval" && (
                <button
                  className="btn btn--primary btn-full"
                  onClick={() => handleStatusChange("approved")}
                >
                  Approve
                </button>
              )}
              {broadcast.status === "approved" && (
                <button
                  className="btn btn--primary btn-full"
                  onClick={() => handleStatusChange("published")}
                >
                  Publish Now
                </button>
              )}
              {(broadcast.status === "draft" ||
                broadcast.status === "pending_approval" ||
                broadcast.status === "cancelled") && (
                <button
                  className="btn btn--danger btn-full"
                  onClick={handleDeleteBroadcast}
                >
                  <Icon name="trash" size={14} /> Delete Broadcast
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Edit Drawer */}
      {editDrawerOpen && (
        <FormDrawer
          open={editDrawerOpen}
          editMode={true}
          initialData={{
            ...broadcast,
            body: broadcast.body || broadcast.message,
          }}
          title="Edit Broadcast"
          fields={getBroadcastFields()}
          onClose={() => setEditDrawerOpen(false)}
          submitLabel="Save Changes"
          onSubmit={handleEditSubmit}
          validate={(f) => {
            if (!f.title?.trim()) return "Title required";
            if (!f.body?.trim()) return "Body required";
            return null;
          }}
        />
      )}

      {/* Confirm Modal */}
      {confirm && (
        <ConfirmModal
          title={confirm.title}
          message={confirm.message}
          confirmLabel={confirm.confirmLabel}
          danger={confirm.danger}
          onConfirm={confirm.onConfirm}
          onClose={() => setConfirm(null)}
        />
      )}

      {/* Toast */}
      {toast && <div className="broadcasts-toast">{toast}</div>}
    </div>
  );
}

export default BroadcastDetails;
